import {t} from "ftc-common/i18n";
import Counter from 'ftc-common/ref/components/Counter'
import Toggle from 'ftc-common/ref/components/Toggle'
import {ArtifactType, DecodeTeleopBaseLocation} from "ftc-common/javaTypes";
import {ScoreType, ScoreTypeSetValue} from "./Decode/scores";
import {useId} from "react";
import './Decode.css'
import {Button, ButtonGroup} from "react-bootstrap";
import {useAsset} from "ftc-common/AssetHelper";
import {ReactSVG} from "react-svg";
import faSquare from "@fortawesome/fontawesome-free/svgs/regular/square.svg";

export const season = "DECODE";

// There is more copy-paste here than usual. Should some stuff get shared between cloud and local for this?
// Or do we not care because then things can evolve separately

export const randomizationArtifacts: Record<number, ArtifactType[]> = {
    [1]: [ArtifactType.GREEN, ArtifactType.PURPLE, ArtifactType.PURPLE],
    [2]: [ArtifactType.PURPLE, ArtifactType.GREEN, ArtifactType.PURPLE],
    [3]: [ArtifactType.PURPLE, ArtifactType.PURPLE, ArtifactType.GREEN]
}

function Artifact({value, matches}: {value: ArtifactType, matches?: boolean}) {
    return <span className={`artifact artifact-${value.toLowerCase()} ${matches ? 'matches' : ''}`}>{({
        [ArtifactType.GREEN]: 'G',
        [ArtifactType.PURPLE]: 'P',
        [ArtifactType.NONE]: '\u00A0',
    })[value]}</span>;
}

function invalidArtifactIndexes(artifacts: ArtifactType[]) {
    const indexes: number[] = [];
    let foundNone = false;
    artifacts.forEach((artifact, index) => {
        if (artifact === ArtifactType.NONE) {
            foundNone = true;
        } else if (foundNone) {
            indexes.push(index);
        }
    });
    return indexes;
}

function replaceClassifierState(state: ArtifactType, position: number, existing: ArtifactType[]): ArtifactType[] {
    const newState = [...existing];
    if (position < 0 || position >= newState.length) {
        throw new Error(`Invalid position ${position} for classifier state`);
    }
    newState[position] = state;
    return newState;
}

const classifierPositions = [1, 2, 3, 4, 5, 6, 7, 8, 9];

function Classifier({ score, setScoreValue, field, random }: { score: ScoreType, setScoreValue: ScoreTypeSetValue, field: 'autoClassifierState' | 'teleopClassifierState', random: number }) {
    const invalidIndexes = invalidArtifactIndexes(score[field]);
    const idPrefix = useId();
    const gateSvg = useAsset('img/decode/gate.svg');
    const targetPattern = random && random > 0 ? randomizationArtifacts[random] : [null, null, null];
    return <div className="row justify-content-center">
        <table className="table table-bordered table-sm col-10 text-center classifier">
            <thead>
            <tr>
                <th>GATE <ReactSVG src={gateSvg} className={"d-inline-block"}/></th>
                <th>GREEN</th>
                <th>PURPLE</th>
                <th>NONE</th>
                <th></th>
            </tr>
            </thead>
            <tbody>
            {classifierPositions.map((pos, idx) => <tr key={pos}
                                                       className={invalidIndexes.includes(idx) ? 'table-danger' : ''}>
                <th>{pos}</th>
                {[ArtifactType.GREEN, ArtifactType.PURPLE, ArtifactType.NONE].map((type) => (
                    <td key={type}>
                        <input type="radio" name={`${idPrefix}-${field}${pos}`} id={`${idPrefix}-${field}${pos}-${type}`}
                               checked={score[field][pos - 1] == type}
                               onChange={(e) => {
                                   e.target.checked ? setScoreValue(field, replaceClassifierState(type, pos - 1, score[field])) : null
                               }}/>
                        <label htmlFor={`${idPrefix}-${field}${pos}-${type}`}
                            className={`btn btn-outline-secondary artifactLabel ` +
                                `${type.toString().toLowerCase()}-artifact ` +
                                `position-${pos} ` +
                                `${score[field][pos - 1] == type ? 'active' : ''}`}>
                            <Artifact value={type}/>
                        </label>
                    </td>))}
                <td style={{verticalAlign: "middle"}}  className={score[field][pos - 1] == targetPattern[idx % 3] ? 'classifiedMatches' : ''}>
                    <div className={"mx-auto"} style={{width: 'fit-content'}}>
                        <Artifact value={score[field][pos - 1]}/>
                    </div>
                </td>
            </tr>)}
            </tbody>
            <tfoot>
            <tr>
                <th>SQUARE <i className={`far fa-fw fa-square`} style={{fontSize: '20px'}}></i></th>
                <th>GREEN</th>
                <th>PURPLE</th>
                <th>NONE</th>
                <th></th>
            </tr>
            </tfoot>
        </table>
    </div>
}

export function Auto({score, setScoreValue, random} : {
    score: ScoreType,
    setScoreValue: ScoreTypeSetValue,
    random: number
}) {
    return <>
        <div className="row justify-content-center">
            <div className={"d-none d-md-block col-md-5 flex-column"}>&nbsp;</div>

            <Counter className={"col-md-5 col-10 flex-column"} label={`OVERFLOW ARTIFACTS`}
                     limit={1000}
                     value={score.autoOverflowArtifacts}
                     setValue={(val) => {
                         setScoreValue("autoOverflowArtifacts", val)
                     }} />

            <div className={"d-none d-md-block col-md-5 flex-column"}>&nbsp;</div>
            <Counter className={"col-md-5 col-10 flex-column"} label={`CLASSIFIED ARTIFACTS`}
                     limit={1000}
                     value={score.autoClassifiedArtifacts}
                     setValue={(val) => {
                         setScoreValue("autoClassifiedArtifacts", val)
                     }} />
        </div>

        <Classifier score={score} setScoreValue={setScoreValue} random={random}
                    field={"autoClassifierState"}/>

        <div className="row justify-content-center">
            <Toggle label={t`ROBOT 1 LEAVE`} options={[t`No`, t`Yes`]}
                    value={score.robot1Auto ? 1 : 0}
                    setValue={(val) => setScoreValue('robot1Auto', val == 1)}/>
            <Toggle label={t`ROBOT 2 LEAVE`} options={[t`No`, t`Yes`]}
                    value={score.robot2Auto ? 1 : 0}
                    setValue={(val) => setScoreValue('robot2Auto', val == 1)}/>
        </div>
    </>
}

const egLocationToInt = {
    [DecodeTeleopBaseLocation.NONE]: 0,
    [DecodeTeleopBaseLocation.PARTIAL]: 1,
    [DecodeTeleopBaseLocation.FULL]: 2,
}
const intToEgLocation = [DecodeTeleopBaseLocation.NONE, DecodeTeleopBaseLocation.PARTIAL, DecodeTeleopBaseLocation.FULL]

export function Teleop({score, setScoreValue, random}: {
    score: ScoreType,
    setScoreValue: ScoreTypeSetValue,
    random: number
}) {

    return <>


        <div className="row justify-content-center">
            <Counter className={"col-md-5 col-10 flex-column"} label={`DEPOT ARTIFACTS`}
                     limit={1000}
                     value={score.teleopDepotArtifacts}
                     setValue={(val) => {
                         setScoreValue("teleopDepotArtifacts", val)
                     }} />

            <Counter className={"col-md-5 col-10 flex-column"} label={`OVERFLOW ARTIFACTS`}
                     limit={1000}
                     value={score.teleopOverflowArtifacts}
                     setValue={(val) => {
                         setScoreValue("teleopOverflowArtifacts", val)
                     }} />

            <div className={"d-none d-md-block col-md-5 flex-column"}>&nbsp;</div>

            <Counter className={"col-md-5 col-10 flex-column"} label={`CLASSIFIED ARTIFACTS`}
                     limit={1000}
                     value={score.teleopClassifiedArtifacts}
                     setValue={(val) => {
                         setScoreValue("teleopClassifiedArtifacts", val)
                     }} />
        </div>

        <Classifier score={score} setScoreValue={setScoreValue} random={random}
                    field={"teleopClassifierState"}/>

        <div className="row justify-content-center">
            <Toggle label={`Robot 1 BASE:`} options={[t`None`, t`PARTIAL`, t`FULL`]} value={egLocationToInt[score.robot1Teleop]} setValue={(val) => setScoreValue('robot1Teleop', intToEgLocation[val])} />
            <Toggle label={`Robot 2 BASE:`} options={[t`None`, t`PARTIAL`, t`FULL`]} value={egLocationToInt[score.robot2Teleop]} setValue={(val) => setScoreValue('robot2Teleop', intToEgLocation[val])} />
        </div>
    </>
}

export function Penalties({score, setScoreValue}: {
    score: ScoreType,
    setScoreValue: ScoreTypeSetValue,
}) {
    return <>
        <div className="row justify-content-center">
            <Counter className={"col-12 col-sm-10 col-lg-5"} label={t`MINOR FOULS:`} value={score.minorFouls}
                     setValue={(val) => setScoreValue('minorFouls', val)}/>
            <Counter className={"col-12 col-sm-10 col-lg-5"} label={t`MAJOR FOULS:`} value={score.majorFouls}
                     setValue={(val) => setScoreValue('majorFouls', val)}/>
        </div>
    </>
}

export function Randomization({random, setRandom}: {random: number, setRandom: (val: number) => void}) {
    const randomizationText = random > 0 ? (["", "MOTIF 1 (ID 21)", "MOTIF 2 (ID 22)", "MOTIF 3 (ID 23)"])[random] : "-";
    const gateSvg = useAsset('img/decode/gate.svg');

    return <>
        <div className="col-11 col-sm-10 text-center">
            Randomization:&nbsp;
            <ButtonGroup className={"me-3 mb-2"}>
                <Button variant={"outline-secondary"} active={random == 1} onClick={() => setRandom(1)}>MOTIF 1 (ID 21)</Button>
                <Button variant={"outline-secondary"} active={random == 2} onClick={() => setRandom(2)}>MOTIF 2 (ID 22)</Button>
                <Button variant={"outline-secondary"} active={random == 3} onClick={() => setRandom(3)}>MOTIF 3 (ID 23)</Button>
            </ButtonGroup>
            <Button className={"mb-2"} variant={"warning"} onClick={() => setRandom(Math.floor(Math.random() * 3) + 1)}>Random</Button>
        </div>
        <div className="col-11 col-sm-10 text-center hr-artifacts">
            <ReactSVG src={gateSvg} className={"randomizer-icon"}></ReactSVG>
            <Artifact value={randomizationText && random ? randomizationArtifacts[random][0] : ArtifactType.NONE} />
            <Artifact value={randomizationText && random ? randomizationArtifacts[random][1] : ArtifactType.NONE} />
            <Artifact value={randomizationText && random ? randomizationArtifacts[random][2] : ArtifactType.NONE} />
            <ReactSVG src={faSquare} className={"randomizer-icon"}></ReactSVG>
        </div>
    </>
}

export function validationErrors(
    score: ScoreType
) {
    const errors = []
    for (const field of Object.keys(score) as (keyof ScoreType)[]) {
        if(typeof score[field] == 'number' && score[field] < 0) {
            errors.push(`Negative value for ${field} alliance`);
        }
        if(field == 'autoClassifierState' || field == 'teleopClassifierState') {
            if(invalidArtifactIndexes(score[field]).length > 0) {
                errors.push(`Artifacts must be contiguous in the ${field.replace('ClassifierState', '')} classifier`);
            }
        }
    }
    return errors;
}